import type { WebEditorLayoutProject, WebEditorProjectSettings } from '../../layout.js';
import { renderGlobalTypographySettings } from './editorTypographyControls.js';

interface CursorEffectDefinition {
  id: string;
  label: string;
  description: string;
  previewClass: string;
}

export interface EditorSettingsModalContext {
  cursorEffectDefinitions: CursorEffectDefinition[];
  handleCursorUpload: (event: Event) => void;
  handleFaviconUpload: (event: Event) => void;
  html: typeof import('lit').html;
  project: WebEditorLayoutProject;
  setShowSettings: (value: boolean) => void;
  showSettings: boolean;
  updateSettings: (settings: Partial<WebEditorProjectSettings>) => void;
}

export const renderEditorSettingsModal = (context: EditorSettingsModalContext) => {
  const {
    cursorEffectDefinitions,
    handleCursorUpload,
    handleFaviconUpload,
    html,
    project,
    setShowSettings,
    showSettings,
    updateSettings,
  } = context;
  if (!showSettings) return '';
  const settings = project.settings ?? {};
  return html`
    <div
      class="vp-editor-overlay"
      @click=${(event: MouseEvent) => {
        if ((event.target as HTMLElement).classList.contains('vp-editor-overlay')) setShowSettings(false);
      }}
    >
      <div class="vp-editor-settings-modal">
        <div class="vp-editor-settings-header">
          <h2>Einstellungen</h2>
          <button type="button" @click=${() => setShowSettings(false)}>
            <span class="icon-[material-symbols--close]"></span>
          </button>
        </div>

        <div class="vp-editor-settings-body">
          <section class="vp-editor-settings-section">
            <h3>SEO & Seite</h3>
            <label>
              Website-Titel
              <input
                type="text"
                .value=${settings.siteTitle ?? ''}
                placeholder="Viva Porta GmbH"
                @input=${(event: InputEvent) => updateSettings({ siteTitle: (event.target as HTMLInputElement).value })}
              />
            </label>
            <label>
              SEO-Beschreibung
              <textarea
                .value=${settings.seoDescription ?? ''}
                rows="3"
                placeholder="Kurze Beschreibung für Suchmaschinen..."
                @input=${(event: InputEvent) =>
                  updateSettings({ seoDescription: (event.target as HTMLTextAreaElement).value })}
              ></textarea>
            </label>
          </section>

          ${renderGlobalTypographySettings(project, updateSettings)}

          <section class="vp-editor-settings-section">
            <h3>Favicon</h3>
            <label>
              URL
              <input
                type="url"
                .value=${settings.faviconUrl ?? ''}
                placeholder="https://..."
                @input=${(event: InputEvent) =>
                  updateSettings({ faviconUrl: (event.target as HTMLInputElement).value })}
              />
            </label>
            <label class="vp-editor-upload-btn">
              <span class="icon-[material-symbols--upload]"></span> Bild hochladen
              <input type="file" accept="image/*" @change=${handleFaviconUpload} />
            </label>
            ${settings.faviconUrl
              ? html`<img class="vp-editor-favicon-preview" src=${settings.faviconUrl} alt="Favicon" />`
              : ''}
          </section>

          <section class="vp-editor-settings-section">
            <h3>Custom Cursor</h3>
            <p class="vp-editor-muted">
              Animierter Cursor auf der veröffentlichten Website. Wähle einen Stil oder lade ein eigenes Bild hoch.
            </p>

            <div class="vp-editor-cursor-grid">
              <button
                type="button"
                class="vp-editor-cursor-card ${!settings.cursorEffect && !settings.cursorCss?.includes('url(')
                  ? 'is-active'
                  : ''}"
                @click=${() => updateSettings({ cursorEffect: undefined })}
              >
                <div class="vp-cursor-preview vp-cursor-preview-none">
                  <span class="icon-[material-symbols--arrow-selector-tool-outline]"></span>
                </div>
                <div class="vp-cursor-card-info">
                  <strong>Standard</strong>
                  <span>Normaler Mauszeiger</span>
                </div>
              </button>

              ${cursorEffectDefinitions.map(
                effect => html`
                  <button
                    type="button"
                    class="vp-editor-cursor-card ${settings.cursorEffect === effect.id ? 'is-active' : ''}"
                    @click=${() => updateSettings({ cursorEffect: effect.id, cursorCss: undefined })}
                  >
                    <div class="vp-cursor-preview ${effect.previewClass}">
                      <div class="vp-cursor-preview-anim"></div>
                    </div>
                    <div class="vp-cursor-card-info">
                      <strong>${effect.label}</strong>
                      <span>${effect.description}</span>
                    </div>
                  </button>
                `,
              )}
            </div>

            <div class="vp-editor-cursor-color-row">
              <label class="vp-editor-cursor-color-label">
                <span>Akzentfarbe</span>
                <div class="vp-editor-color-wrap">
                  <input
                    type="color"
                    .value=${settings.cursorColor ?? '#48c5ce'}
                    @input=${(event: InputEvent) =>
                      updateSettings({ cursorColor: (event.target as HTMLInputElement).value })}
                  />
                  <span class="vp-editor-color-hex">${settings.cursorColor ?? '#48c5ce'}</span>
                </div>
              </label>
            </div>

            <label class="vp-editor-upload-btn">
              <span class="icon-[material-symbols--upload]"></span> Eigenes Cursor-Bild (PNG/SVG)
              <input type="file" accept="image/png,image/svg+xml,image/webp" @change=${handleCursorUpload} />
            </label>

            ${settings.cursorCss?.includes('url(')
              ? html`
                  <div class="vp-editor-cursor-active">
                    <span class="icon-[material-symbols--check-circle-outline]"></span>
                    Eigenes Cursor-Bild aktiv
                    <button type="button" @click=${() => updateSettings({ cursorCss: undefined })}>
                      <span class="icon-[material-symbols--close]"></span>
                    </button>
                  </div>
                `
              : ''}
          </section>
        </div>
      </div>
    </div>
  `;
};
